import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import BookingsTab from "@/pages/admin/BookingsTab";
import SettingsTab from "@/pages/admin/SettingsTab";
import ServicesTab from "@/pages/admin/ServicesTab";
import GalleryTab from "@/pages/admin/GalleryTab";
import ReviewsTab from "@/pages/admin/ReviewsTab";
import SiteTab from "@/pages/admin/SiteTab";

const TABS = [
  ["bookings", "Bookings"],
  ["availability", "Availability"],
  ["services", "Services"],
  ["gallery", "Gallery"],
  ["reviews", "Reviews"],
  ["site", "Site"],
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [data, setData] = useState({ bookings: [], settings: null, services: [], gallery: [], reviews: [], enquiries: [], content: {} });

  const load = useCallback(async () => {
    try {
      const [bookings, settings, services, gallery, reviews, enquiries, content] = await Promise.all([
        api.get("/admin/bookings"),
        api.get("/admin/settings"),
        api.get("/services"),
        api.get("/gallery"),
        api.get("/admin/reviews"),
        api.get("/admin/enquiries"),
        api.get("/content"),
      ]);
      setData({
        bookings: bookings.data, settings: settings.data, services: services.data,
        gallery: gallery.data, reviews: reviews.data, enquiries: enquiries.data, content: content.data,
      });
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("sy_admin_token");
        navigate("/admin/login");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("sy_admin_token")) {
      navigate("/admin/login");
      return;
    }
    api.get("/auth/me")
      .then(() => load().finally(() => setChecking(false)))
      .catch(() => {
        localStorage.removeItem("sy_admin_token");
        navigate("/admin/login");
      });
  }, [navigate, load]);

  const logout = () => {
    localStorage.removeItem("sy_admin_token");
    navigate("/admin/login");
  };

  if (checking || !data.settings)
    return <div className="bg-[#070b12] min-h-screen flex items-center justify-center text-[#93b4d4] font-mono-lbl text-sm" data-testid="admin-loading">Loading dashboard…</div>;

  const pendingCount = data.bookings.filter((b) => b.status === "pending").length;

  return (
    <div className="bg-[#070b12] min-h-screen text-[#f0f4f8]" data-testid="admin-dashboard">
      <header className="border-b border-[#1e9bff]/20 bg-[#070b12]/90 sticky top-0 z-40 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/"><Logo className="w-7 h-7" /></Link>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site-link" className="text-xs font-mono-lbl text-[#93b4d4] hover:text-white transition-colors uppercase tracking-wider">
              View Site
            </Link>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="px-4 py-2 rounded-full border border-[#1e9bff]/40 text-[#1e9bff] text-xs font-mono-lbl uppercase tracking-wider hover:bg-[#1e9bff]/10 transition-colors">
              Log Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-display uppercase text-3xl md:text-4xl text-white">Dashboard</h1>
            <p className="text-xs font-mono-lbl text-[#93b4d4] mt-1 uppercase tracking-widest">
              {pendingCount > 0 ? `${pendingCount} booking${pendingCount > 1 ? "s" : ""} waiting for approval` : "All caught up"}
            </p>
          </div>
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="bg-[#0b1322] border border-[#1e9bff]/20 flex-wrap h-auto mb-8" data-testid="admin-tabs">
            {TABS.map(([key, label]) => (
              <TabsTrigger key={key} value={key} data-testid={`admin-tab-${key}`}
                className="font-mono-lbl text-xs uppercase tracking-wider data-[state=active]:bg-[#1e9bff] data-[state=active]:text-black">
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="bookings"><BookingsTab bookings={data.bookings} reload={load} /></TabsContent>
          <TabsContent value="availability"><SettingsTab settings={data.settings} reload={load} /></TabsContent>
          <TabsContent value="services"><ServicesTab services={data.services} reload={load} /></TabsContent>
          <TabsContent value="gallery"><GalleryTab gallery={data.gallery} services={data.services} reload={load} /></TabsContent>
          <TabsContent value="reviews"><ReviewsTab reviews={data.reviews} reload={load} /></TabsContent>
          <TabsContent value="site"><SiteTab content={data.content} enquiries={data.enquiries} reload={load} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
